import { motion } from "framer-motion";
import {
  ArrowRight,
  Backpack,
  Brain,
  Clock,
  DollarSign,
  HeartHandshake,
  HeartPulse,
  Mail,
  MessageCircle,
  Sparkles,
  Users,
} from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Contact";
import { Asterisk, EASE, Reveal } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL = "nbenachour@milestonepediatrictherapyllc.com";

const SKILLS = [
  {
    icon: Users,
    title: "Social & Friendship Skills",
    items: ["Peer interaction", "Starting conversations", "Taking turns", "Sharing", "Cooperative play", "Personal space", "Making and maintaining friendships"],
  },
  {
    icon: MessageCircle,
    title: "Communication & Comprehension",
    items: ["Functional communication", "Asking and answering questions", "Following conversations", "Understanding directions", "Listening and responding", "Expressing wants and needs"],
  },
  {
    icon: Brain,
    title: "Social Understanding",
    items: ["Reading social cues", "Recognizing emotions", "Perspective taking", "Flexible thinking", "Social problem-solving", "Understanding expected behaviors"],
  },
  {
    icon: HeartPulse,
    title: "Emotional & Behavioral Skills",
    items: ["Coping with frustration", "Waiting", "Accepting \u201cno\u201d", "Handling changes", "Self-regulation", "Managing disagreements", "Appropriate responses to challenging situations"],
  },
  {
    icon: Backpack,
    title: "Independence & School Readiness",
    items: ["Following group routines", "Transitions", "Completing activities", "Asking for help", "Working independently", "Participating appropriately in group activities"],
    featured: true,
  },
];

const APPROACH = [
  { icon: Users, title: "Small Groups", text: "Limited group sizes so every child gets meaningful attention and practice." },
  { icon: HeartHandshake, title: "Meaningful Connections", text: "Groups formed by age, communication level, developmental needs, and social goals." },
  { icon: Sparkles, title: "Real-Life Practice", text: "Games, group projects, role-playing, conversations, and cooperative problem-solving." },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function SocialSkillsGroup() {
  usePageMeta(
    "Social Skills Group | Milestone Pediatric Therapy",
    "Evening social skills ABA groups — small groups, meaningful connections, real-life practice. Teen class Tuesdays & Thursdays, 6:00–7:30 PM. $45/hour self-pay."
  );
  return (
    <div data-testid="social-skills-page" className="min-h-screen bg-background">
      <Nav />
      <main>
        <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
          <div className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-brand-pale/80 blur-3xl" aria-hidden="true" />
          <div className="absolute top-1/2 -left-48 h-80 w-80 rounded-full bg-brand-yellow/30 blur-3xl" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-brand-lightblue/60 bg-white/70 backdrop-blur px-5 py-2 mb-8"
              data-testid="social-eyebrow"
            >
              <Asterisk className="h-3.5 w-3.5 text-brand-coral" />
              <span className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-brand-blue">
                Group ABA Program
              </span>
            </motion.div>

            <h1 className="font-serif font-medium tracking-tight text-brand-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl" data-testid="social-heading">
              <MaskedLine delay={0.15}>Social Skills</MaskedLine>
              <MaskedLine delay={0.3}>
                <em className="text-brand-coral">Group</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
              className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-brand-muted"
              data-testid="social-subcopy"
            >
              Structured, supportive group sessions where children build friendships, communication,
              and confidence — together.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
              data-testid="social-quick-facts"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-lightblue/50 px-6 py-3 text-sm font-semibold text-brand-blue shadow-soft">
                <Clock className="h-4 w-4 text-brand-coral" /> Teen Class: Tuesdays & Thursdays, 6:00 PM – 7:30 PM
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-lightblue/50 px-6 py-3 text-sm font-semibold text-brand-blue shadow-soft">
                <DollarSign className="h-4 w-4 text-brand-coral" /> Self-Pay Rate: $45/hour
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <a
                href={`mailto:${EMAIL}?subject=Social%20Skills%20Group%20Enrollment`}
                data-testid="social-cta-enroll"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-[#f06666]"
              >
                Ask About Enrollment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="social-cta-pricing"
                onClick={() => scrollToId("pricing")}
                className="inline-flex items-center gap-2 rounded-full border border-brand-blue/30 text-brand-blue font-semibold px-8 py-4 transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:bg-brand-pale hover:border-brand-blue/50"
              >
                See Pricing
              </button>
            </motion.div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white/60 border-y border-brand-lightblue/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <Asterisk className="h-4 w-4 text-brand-coral" />
                <span className="text-sm uppercase tracking-[0.22em] font-medium text-brand-coral">
                  Skills We Target
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue mb-14" data-testid="skills-heading">
                Skills that carry into <em className="text-brand-coral">everyday life.</em>
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="skills-grid">
              {SKILLS.map((s, i) => (
                <Reveal key={s.title} delay={0.07 * i}>
                  <article
                    data-testid={`skill-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`group h-full rounded-[2rem] border p-8 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-lift ${
                      s.featured
                        ? "bg-brand-blue text-white border-brand-blue shadow-soft"
                        : "bg-white border-brand-lightblue/40 shadow-soft hover:border-brand-coral/40"
                    }`}
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl mb-6 transition-transform duration-500 group-hover:scale-110 ${
                        s.featured ? "bg-white/15 text-brand-yellow" : "bg-brand-pale text-brand-blue"
                      }`}
                    >
                      <s.icon className="h-6 w-6" />
                    </span>
                    <h3 className={`font-serif text-2xl font-semibold mb-5 ${s.featured ? "text-white" : "text-brand-blue"}`}>
                      {s.title}
                    </h3>
                    <ul className="space-y-2.5">
                      {s.items.map((item) => (
                        <li key={item} className={`flex items-start gap-2.5 text-sm md:text-base ${s.featured ? "text-white/85" : "text-brand-muted"}`}>
                          <span className={`mt-2 h-1.5 w-1.5 rounded-full shrink-0 ${s.featured ? "bg-brand-yellow" : "bg-brand-coral"}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue text-center mb-6" data-testid="approach-heading">
                Small Groups <em className="text-brand-coral">•</em> Meaningful Connections <em className="text-brand-coral">•</em> Real-Life Practice
              </h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-8 mt-14" data-testid="approach-grid">
              {APPROACH.map((a, i) => (
                <Reveal key={a.title} delay={0.08 * i}>
                  <div className="text-center px-4">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-pale text-brand-blue mb-5">
                      <a.icon className="h-7 w-7" />
                    </span>
                    <h3 className="font-serif text-2xl font-semibold text-brand-blue mb-3">{a.title}</h3>
                    <p className="text-sm md:text-base leading-relaxed text-brand-muted">{a.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-14 max-w-3xl mx-auto text-center text-sm md:text-base leading-relaxed text-brand-muted" data-testid="approach-note">
                Children may participate in games, group projects, role-playing, conversations,
                cooperative activities, problem-solving exercises, and structured peer interactions.
                Groups are formed based on factors such as age, communication level, developmental
                needs, and social goals to encourage meaningful and appropriate peer interaction.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 max-w-3xl mx-auto rounded-3xl bg-brand-blue text-white px-8 py-7 text-center shadow-soft" data-testid="adult-program-note">
                <p className="text-sm md:text-base leading-relaxed">
                  <span className="font-semibold text-brand-yellow">New:</span> Ask about our{" "}
                  <span className="font-semibold">Evening Social Skills ABA Program for adults (18+)</span>{" "}
                  — evenings, 6:00 PM – 7:30 PM, building social confidence and connection beyond the
                  school years. Contact us for details.{" "}
                  <a href="/ages-programs" data-testid="adult-program-link" className="underline underline-offset-4 decoration-brand-yellow/60 hover:decoration-brand-yellow transition-[text-decoration-color] duration-300">
                    See all ages & programs
                  </a>
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="pricing" className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-3 mb-5 justify-center">
                <Asterisk className="h-4 w-4 text-brand-coral" />
                <span className="text-sm uppercase tracking-[0.22em] font-medium text-brand-coral">
                  Enrollment & Pricing
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue text-center mb-14" data-testid="pricing-heading">
                Simple <em className="text-brand-coral">self-pay</em> pricing.
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-testid="pricing-grid">
              <Reveal>
                <div className="h-full rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft p-8 text-center transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-lift hover:border-brand-coral/40" data-testid="pricing-hourly">
                  <p className="text-sm uppercase tracking-[0.18em] font-semibold text-brand-muted mb-4">Per Hour</p>
                  <p className="font-serif text-5xl font-semibold text-brand-blue">$45</p>
                  <p className="mt-3 text-sm text-brand-muted">Flexible hourly self-pay rate</p>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="h-full rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft p-8 text-center transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-lift hover:border-brand-coral/40" data-testid="pricing-2x">
                  <p className="text-sm uppercase tracking-[0.18em] font-semibold text-brand-muted mb-4">2× Weekly</p>
                  <p className="font-serif text-5xl font-semibold text-brand-blue">$540</p>
                  <p className="mt-3 text-sm text-brand-muted">per month</p>
                </div>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="h-full rounded-[2rem] bg-brand-blue text-white border border-brand-blue shadow-lift p-8 text-center transition-[transform,box-shadow] duration-500 hover:-translate-y-2" data-testid="pricing-3x">
                  <p className="text-sm uppercase tracking-[0.18em] font-semibold text-brand-yellow mb-4">3× Weekly</p>
                  <p className="font-serif text-5xl font-semibold">$810</p>
                  <p className="mt-3 text-sm text-white/80">per month</p>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.2}>
              <div className="mt-10 max-w-3xl mx-auto rounded-3xl bg-brand-pale/60 border border-brand-lightblue/40 px-8 py-6 text-center space-y-2">
                <p className="text-sm font-semibold text-brand-blue" data-testid="pricing-enrollment-note">
                  Limited Group Size • Monthly Enrollment
                </p>
                <p className="text-sm text-brand-muted" data-testid="pricing-footnote">
                  *Monthly rates are based on approximately 4 weeks of programming.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="rounded-[2.5rem] bg-brand-blue text-white shadow-lift p-10 md:p-16 text-center" data-testid="social-enroll-cta">
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-5">
                  Ready to <em className="text-brand-yellow">join the group?</em>
                </h2>
                <p className="max-w-2xl mx-auto text-white/85 text-base md:text-lg leading-relaxed mb-9">
                  Contact Milestone Pediatric Therapy for enrollment and availability.
                </p>
                <a
                  href={`mailto:${EMAIL}?subject=Social%20Skills%20Group%20Enrollment`}
                  data-testid="social-enroll-email-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-white text-brand-blue font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift"
                >
                  <Mail className="h-4 w-4" />
                  {EMAIL}
                </a>
                <p className="mt-8 text-xs text-white/60 max-w-xl mx-auto" data-testid="social-disclaimer">
                  Participation is based on individual needs and appropriateness for the group. ABA
                  services are provided with appropriate clinical oversight and in accordance with
                  applicable professional requirements.
                </p>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
